import * as React from 'react';
import TableBody from '@mui/material/TableBody';
import { GeneralTableToolbar } from './components/GeneralTableToolbar';
import { HeadCell, Order, RowItems } from '../../../models/TableProps';
import { TableHeadWrapper } from './components/TableHeadWrapper';
import { TableContentWrapper } from './components/TableContentWrapper';
import { TableEmptyRows } from './components/TableEmptyRows';
import { FunctionComponent, useEffect } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import TablePagination from '@mui/material/TablePagination';
import { useTranslation } from 'react-i18next';
import { TableLoadingLayout } from '../../layouts/LoadingLayouts/TableLoadingLayout';
import { Skeleton, TableCell, TableRow } from '@mui/material';

export const SWRTable = <T, S>(props: QuotationsProps<T, S>) => {
    const {
        listItems,
        totalNumber,
        component,
        handleChange,
        paginateOptions,
        defaultOrderBy,
        selectHandleId,
        tableSettings,
        forceRefresh,
        isLoading,
    } = props;
    const [order, setOrder] = React.useState<Order>('asc');
    const [orderBy, setOrderBy] = React.useState<keyof S>(defaultOrderBy);
    const [selected, setSelected] = React.useState([]);
    const [rows, setRows] = React.useState(listItems);
    const [page, setPage] = React.useState(paginateOptions.page);
    const [rowsPerPage, setRowsPerPage] = React.useState(paginateOptions.size);
    const [search, setSearch] = React.useState(paginateOptions.search);
    const { t } = useTranslation();
    let delayTimer;

    useEffect(() => {
        if (listItems) {
            setRows(listItems);
        }
    }, [listItems, isLoading]);

    const handleRequestSort = (event: React.MouseEvent<unknown>, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
        //TODO: megcsinálni hogy ez is működjön majd egységesen
    };

    const handleSelectAllClick = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        if (event.target.checked) {
            const newSelected = rows.map((n) => n[selectHandleId]);
            setSelected(newSelected);
            return;
        }
        setSelected([]);
    };

    const handleClick = (event: React.MouseEvent<unknown>, name: string) => {
        const selectedIndex = selected.indexOf(name);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, name);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selected.slice(0, selectedIndex),
                selected.slice(selectedIndex + 1)
            );
        }

        setSelected(newSelected);
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
        handleChange(rowsPerPage, newPage, search);
    };

    const handleFilter = (searchItem) => {
        clearTimeout(delayTimer);
        delayTimer = setTimeout(function () {
            if (searchItem.length > 0) {
                setSearch(searchItem);
                handleChange(rowsPerPage, page, searchItem);
            } else {
                setSearch('');
                handleChange(rowsPerPage, page, searchItem);
            }
        }, 1000);
    };

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
        handleChange(parseInt(event.target.value, 10), 0, search);
    };

    const handleForceRefresh = () => {
        forceRefresh();
    };

    const loading = () => {
        return (
            <>
                {Array.from(Array(rowsPerPage), (e, i) => {
                    return (
                        <TableRow key={i}>
                            <TableCell
                                colSpan={
                                    tableSettings.tableHeadSettings.headContent
                                        .length + 1
                                }
                            >
                                <Skeleton
                                    variant="text"
                                    sx={{ fontSize: '1rem' }}
                                />
                            </TableCell>
                        </TableRow>
                    );
                })}
            </>
        );
    };

    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows = page > 0 ? rowsPerPage - rows.length : 0;

    return (
        <Box sx={{ width: '100%' }}>
            <Paper sx={{ width: '100%', mb: 2 }}>
                <GeneralTableToolbar
                    numSelected={selected.length}
                    onFilter={handleFilter}
                    tableToolbarSettings={tableSettings.tableToolbarSettings}
                />
                <TableContentWrapper>
                    <TableHeadWrapper<S>
                        selectable={tableSettings.tableHeadSettings.selectable}
                        onSelectAllClick={handleSelectAllClick}
                        order={order}
                        orderBy={orderBy}
                        numSelected={selected.length}
                        rowCount={rows.length}
                        onRequestSort={handleRequestSort}
                        headContent={
                            tableSettings.tableHeadSettings.headContent
                        }
                        translateNs={
                            tableSettings.tableHeadSettings.translateNs
                        }
                        translatePrefix={
                            tableSettings.tableHeadSettings.translatePrefix
                        }
                    />
                    <TableBody>
                        {isLoading && loading()}
                        {!isLoading &&
                            rows &&
                            rows.map((row, index) => {
                                return React.createElement(component, {
                                    key: index,
                                    row: row,
                                    index: index,
                                    clickHandle: handleClick,
                                    selected: selected,
                                });
                            })}

                        <TableEmptyRows
                            emptyRows={emptyRows}
                            dense={false}
                            colSpan={10}
                        />
                    </TableBody>
                </TableContentWrapper>
                <TablePagination
                    rowsPerPageOptions={[2, 5, 10, 25, 50, 100]}
                    component="div"
                    count={
                        paginateOptions.lazyPaginate ? totalNumber : rows.length
                    }
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage={t('TABLE_PAGINATOR.ROWS_PER_PAGE')}
                    labelDisplayedRows={({ from, to, count }) => {
                        return t('TABLE_PAGINATOR.ROWS', {
                            from: from,
                            to: to,
                            count: count,
                        });
                    }}
                    showFirstButton
                    showLastButton
                />
            </Paper>
        </Box>
    );
};

interface QuotationsProps<T, S> {
    listItems: T[];
    totalNumber: number;
    component: FunctionComponent<RowItems<T>>;
    handleChange: (size: number, page: number, filter: string) => void;
    paginateOptions: {
        lazyPaginate: boolean;
        size: number;
        page: number;
        search: string;
    };
    // filterTerm(search: string): T[];
    defaultOrderBy?: keyof S;
    selectHandleId?: keyof T;
    tableSettings: {
        tableHeadSettings: {
            headContent: HeadCell<S>[];
            translateNs: string;
            translatePrefix: string;
            selectable: boolean;
        };
        tableToolbarSettings: {
            translateNs: string;
            translatePrefix: string;
        };
    };
    forceRefresh: () => void;
    isLoading: boolean;
}
